import {DuplicateIcon} from '@primer/octicons-react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {UnmarkedAsDuplicateEvent$key} from './__generated__/UnmarkedAsDuplicateEvent.graphql'
import type {IssueLinkFragment} from './IssueLink'
import {IssueLink} from './IssueLink'
import {TimelineRow} from './row/TimelineRow'

type UnmarkedAsDuplicateEventProps = {
  queryRef: UnmarkedAsDuplicateEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  repositoryId: string
  ownerLogin: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
}

export function UnmarkedAsDuplicateEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  repositoryId,
  ownerLogin,
}: UnmarkedAsDuplicateEventProps): React.ReactElement {
  const {
    actor,
    createdAt,
    canonical,
    databaseId,
    isCanonicalOfClosedDuplicate: isCanonicalDuplicate,
  } = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment UnmarkedAsDuplicateEvent on UnmarkedAsDuplicateEvent {
        actor {
          avatarUrl(size: 64)
          login
          profileResourcePath
          __typename
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
          ... on User {
            name
          }
          ... on EnterpriseUserAccount {
            name
          }
          ... on Mannequin {
            name
          }
        }
        createdAt
        canonical {
          ... on Issue {
            __typename
            id
            titleHTML
            url
            number
            stateReason
            repository {
              id
              name
              isPrivate
              owner {
                login
              }
            }
          }
          ... on PullRequest {
            __typename
            id
            pullTitleHTML: titleHTML
            url
            number
            state
            isDraft
            isInMergeQueue
            repository {
              id
              name
              isPrivate
              owner {
                login
              }
            }
          }
        }
        isCanonicalOfClosedDuplicate
        databaseId
      }
    `,
    queryRef,
  )
  const highlighted = String(databaseId) === highlightedEventId

  if (!canonical) return <></>

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={DuplicateIcon}
    >
      <TimelineRow.Main>
        {isCanonicalDuplicate ? (
          <>
            unmarked{' '}
            {/* RELAY-CLEANUP(UnmarkedAsDuplicateEvent): boundary cast; remove when UnmarkedAsDuplicateEvent leaves Relay. */}
            <IssueLink
              inline
              data={canonical as IssueLinkFragment}
              targetRepositoryId={repositoryId}
              targetOwnerLogin={ownerLogin}
            />{' '}
            as a duplicate of this issue{' '}
          </>
        ) : (
          <>
            {LABELS.timeline.unmarkedAsDuplicate}
            <>
              &nbsp;
              {/* RELAY-CLEANUP(UnmarkedAsDuplicateEvent): boundary cast; remove when UnmarkedAsDuplicateEvent leaves Relay. */}
              <IssueLink
                data={canonical as IssueLinkFragment}
                targetRepositoryId={repositoryId}
                targetOwnerLogin={ownerLogin}
              />{' '}
            </>
          </>
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}
